import { useEffect, useState } from 'react';
import { Mail, User, KeyRound, Calendar, Building2, Shield, Info } from 'lucide-react';
import { api } from '@/lib/api';
import { LoadingSpinner } from '@/components/shared/LoadingSpinner';
import type { AccountResponse, AccountMembership } from '@/types/api';

function formatDate(iso: string): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
}

function signInMethodLabel(m: AccountResponse['user']['signInMethod']): string {
  return m === 'entra' ? 'Microsoft (Entra ID)' : 'Email + password';
}

function roleLabel(role: string): string {
  return role ? role.charAt(0).toUpperCase() + role.slice(1) : '';
}

function pickActiveMembership(memberships: AccountMembership[]): AccountMembership | null {
  return memberships.find((m) => m.isActive) ?? memberships[0] ?? null;
}

export function AccountPage() {
  const [data, setData] = useState<AccountResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    api.me.account()
      .then((res) => { if (!cancelled) setData(res); })
      .catch((e: unknown) => {
        if (cancelled) return;
        const msg = e instanceof Error ? e.message : 'Failed to load account';
        setError(msg);
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  if (loading) return <LoadingSpinner message="Loading account..." />;

  if (error || !data) {
    return (
      <div className="max-w-3xl mx-auto">
        <div className="bg-red-50 border border-red-200 rounded-lg px-4 py-3 text-sm text-red-700">
          {error || 'Failed to load account.'}
        </div>
      </div>
    );
  }

  const { user, memberships } = data;
  const activeMembership = pickActiveMembership(memberships);
  const activeTitle = activeMembership?.roleTitle ?? null;
  const isSsoManaged = user.signInMethod === 'entra';

  return (
    <div className="max-w-3xl mx-auto space-y-6">
      {/* Header */}
      <div>
        <p className="text-[10px] font-semibold text-brand-orange uppercase tracking-widest">
          Account
        </p>
        <h1 className="text-xl font-bold text-brand-charcoal mt-1">Your account</h1>
        <p className="text-sm text-slate-500 mt-1">
          Confirm who you're signed in as and which organizations you can access.
        </p>
      </div>

      {/* SSO-managed affordance */}
      {isSsoManaged && (
        <div className="flex items-start gap-2.5 bg-orange-50/60 border border-orange-100 rounded-lg px-4 py-3">
          <Info className="w-4 h-4 text-brand-orange mt-0.5 shrink-0" />
          <p className="text-xs text-slate-700">
            Your name and email are managed by your organization. To change them,
            contact your IT administrator.
          </p>
        </div>
      )}

      {/* Identity card */}
      <div className="bg-white rounded-xl border border-slate-200 p-6 space-y-5">
        <h2 className="text-sm font-semibold text-brand-charcoal">Identity</h2>

        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <User className="w-4 h-4 text-slate-400" />
            <span className="text-sm text-slate-500">Display name</span>
          </div>
          <span className="text-sm font-medium text-brand-charcoal">
            {user.displayName || '—'}
          </span>
        </div>

        <div className="border-t border-slate-100" />

        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Mail className="w-4 h-4 text-slate-400" />
            <span className="text-sm text-slate-500">Email</span>
          </div>
          <span className="text-sm font-medium text-brand-charcoal">{user.email}</span>
        </div>

        {activeTitle && (
          <>
            <div className="border-t border-slate-100" />
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Shield className="w-4 h-4 text-slate-400" />
                <span className="text-sm text-slate-500">Title</span>
              </div>
              <span className="text-sm font-medium text-brand-charcoal">{activeTitle}</span>
            </div>
          </>
        )}

        <div className="border-t border-slate-100" />

        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <KeyRound className="w-4 h-4 text-slate-400" />
            <span className="text-sm text-slate-500">Sign-in method</span>
          </div>
          <span className="text-sm font-medium text-brand-charcoal">
            {signInMethodLabel(user.signInMethod)}
          </span>
        </div>

        <div className="border-t border-slate-100" />

        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Calendar className="w-4 h-4 text-slate-400" />
            <span className="text-sm text-slate-500">Joined</span>
          </div>
          <span className="text-sm font-medium text-brand-charcoal">
            {formatDate(user.joinedAt) || '—'}
          </span>
        </div>
      </div>

      {/* Organizations card */}
      <div className="bg-white rounded-xl border border-slate-200 p-6 space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="text-sm font-semibold text-brand-charcoal">Organizations</h2>
          <span className="text-xs text-slate-400">
            {memberships.length === 0
              ? 'No active memberships'
              : `${memberships.length} active`}
          </span>
        </div>

        {memberships.length === 0 ? (
          <p className="text-sm text-slate-500">
            You're not currently a member of any organization.
          </p>
        ) : (
          <ul className="divide-y divide-slate-100">
            {memberships.map((m) => (
              <li
                key={m.orgId}
                className="py-3 first:pt-0 last:pb-0 flex items-start gap-3"
              >
                <Building2 className="w-4 h-4 text-slate-400 mt-0.5 shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <p className="text-sm font-medium text-brand-charcoal truncate">
                      {m.orgName}
                    </p>
                    {m.isActive && (
                      <span className="text-[10px] font-semibold uppercase tracking-wider bg-orange-100 text-brand-orange px-1.5 py-0.5 rounded">
                        Active
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-slate-500 mt-0.5">
                    {roleLabel(m.role)}
                    {m.roleTitle ? ` · ${m.roleTitle}` : ''}
                    {' · Joined '}
                    {formatDate(m.joinedAt)}
                  </p>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
